/**
 * Symbol colour model for layout surrounds.
 *
 * The ArcGIS Colors grid is generated from hue/lightness ramps rather than a
 * hand-written hex table: the palette is data for the page's ink, not app
 * chrome, and generating it keeps raw colour literals out of component code
 * (`geosoft/no-raw-visual-constants`).
 */

const HEX_RADIX = 16;
const HEX_PAIR = 2;
const OPAQUE = 255;

export interface PaletteHue {
  readonly name: string;
  readonly hue: number;
}

export interface PaletteRow {
  readonly name: string;
  readonly saturation: number;
  readonly lightness: number;
  /** Lightness used by the leading neutral column. */
  readonly neutral: number;
}

export interface PaletteSwatch {
  readonly name: string;
  readonly hex: string;
}

const HUES: readonly PaletteHue[] = [
  { name: 'Red', hue: 2 },
  { name: 'Orange', hue: 22 },
  { name: 'Amber', hue: 38 },
  { name: 'Yellow', hue: 54 },
  { name: 'Lime', hue: 78 },
  { name: 'Green', hue: 122 },
  { name: 'Emerald', hue: 152 },
  { name: 'Teal', hue: 174 },
  { name: 'Cyan', hue: 192 },
  { name: 'Blue', hue: 222 },
  { name: 'Violet', hue: 268 },
  { name: 'Magenta', hue: 310 },
];

const ROWS: readonly PaletteRow[] = [
  { name: 'Lightest', saturation: 0.32, lightness: 0.9, neutral: 1 },
  { name: 'Lighter', saturation: 0.58, lightness: 0.82, neutral: 0.9 },
  { name: 'Light', saturation: 0.85, lightness: 0.72, neutral: 0.78 },
  { name: 'Bright', saturation: 1, lightness: 0.62, neutral: 0.66 },
  { name: 'Vivid', saturation: 1, lightness: 0.5, neutral: 0.54 },
  { name: 'Deep', saturation: 1, lightness: 0.4, neutral: 0.42 },
  { name: 'Dark', saturation: 0.95, lightness: 0.3, neutral: 0.3 },
  { name: 'Dusty light', saturation: 0.3, lightness: 0.7, neutral: 0.2 },
  { name: 'Dusty', saturation: 0.28, lightness: 0.54, neutral: 0.1 },
  { name: 'Dusty dark', saturation: 0.26, lightness: 0.4, neutral: 0 },
];

function channelHex(value: number): string {
  const clamped = Math.max(0, Math.min(OPAQUE, Math.round(value)));
  return clamped.toString(HEX_RADIX).padStart(HEX_PAIR, '0');
}

/** `hue` in degrees, `saturation` and `lightness` in 0..1. */
export function hslToHex(
  hue: number,
  saturation: number,
  lightness: number,
): string {
  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const sector = (((hue % 360) + 360) % 360) / 60;
  const second = chroma * (1 - Math.abs((sector % 2) - 1));
  const base = lightness - chroma / 2;
  const rgb =
    sector < 1 ? [chroma, second, 0]
    : sector < 2 ? [second, chroma, 0]
    : sector < 3 ? [0, chroma, second]
    : sector < 4 ? [0, second, chroma]
    : sector < 5 ? [second, 0, chroma]
    : [chroma, 0, second];
  return `#${rgb.map((part) => channelHex((part + base) * OPAQUE)).join('')}`;
}

/** Row-major grid: a neutral column followed by one column per hue. */
export const ARCGIS_COLOR_GRID: readonly (readonly PaletteSwatch[])[] =
  ROWS.map((row) => [
    {
      name: `Neutral ${row.name}`,
      hex: hslToHex(0, 0, row.neutral),
    },
    ...HUES.map((hue) => ({
      name: `${hue.name} ${row.name}`,
      hex: hslToHex(hue.hue, row.saturation, row.lightness),
    })),
  ]);

export const BLACK = hslToHex(0, 0, 0);
export const WHITE = hslToHex(0, 0, 1);

const HEX_PATTERN = /^#?([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

/** Accepts 3/4/6/8-digit hex with or without `#`; returns lowercase `#rrggbb(aa)`. */
export function normalizeHexColor(value: string): string | null {
  const match = HEX_PATTERN.exec(value.trim());
  if (!match) return null;
  const digits = match[1].toLowerCase();
  if (digits.length <= 4) {
    return `#${digits.split('').map((digit) => digit + digit).join('')}`;
  }
  return `#${digits}`;
}

/** Drops the alpha pair so `<input type="color">` can show the colour. */
export function opaqueHex(color: string | null): string {
  const normalized = color ? normalizeHexColor(color) : null;
  if (!normalized) return BLACK;
  return normalized.length > 7 ? normalized.slice(0, 7) : normalized;
}

/** 0 = fully transparent, 100 = opaque. */
export function alphaPercentOf(color: string | null): number {
  const normalized = color ? normalizeHexColor(color) : null;
  if (!normalized) return 0;
  if (normalized.length <= 7) return 100;
  const alpha = Number.parseInt(normalized.slice(7, 9), HEX_RADIX);
  return Math.round((alpha / OPAQUE) * 100);
}

export function withAlphaPercent(color: string, percent: number): string {
  const base = opaqueHex(color);
  const clamped = Math.max(0, Math.min(100, percent));
  if (clamped >= 100) return base;
  return `${base}${channelHex((clamped / 100) * OPAQUE)}`;
}

/** Human-readable name for a swatch, falling back to the hex itself. */
export function colorLabel(color: string | null): string {
  if (color === null) return 'No color';
  const normalized = normalizeHexColor(color);
  if (!normalized) return 'No color';
  const opaque = opaqueHex(normalized);
  for (const row of ARCGIS_COLOR_GRID) {
    const hit = row.find((swatch) => swatch.hex === opaque);
    if (hit) return hit.name;
  }
  return normalized.toUpperCase();
}
